import { logger } from '@vagabondinternet/logging';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type GamesApi, type GamesList, lobbyFailure } from '../api/games';
import { useSession } from '../session/session-context';
import { documentVisibility, type PageVisibility } from './page-visibility';

/** How often the games list is fetched again while it is visible. */
export const MY_GAMES_POLL_MS = 30_000;

/**
 * - `loading`: until the list first arrives.
 * - `ready`: loaded. A refresh that fails keeps the last list.
 * - `load-error`: the first load failed, until `refresh` succeeds.
 */
export type MyGamesStatus = 'loading' | 'ready' | 'load-error';

export interface MyGamesState {
  status: MyGamesStatus;
  games?: GamesList;
  /** Fetches the list again now, e.g. after a cancel. */
  refresh: () => void;
}

export interface UseMyGamesOptions {
  pollMs?: number;
  visibility?: PageVisibility;
}

/**
 * The signed-in player's games, kept current by polling rather than subscriptions (the API scales to zero and runs
 * one instance): fetched when the screen opens, when the tab is shown or the window regains focus, and every
 * `pollMs` while the tab is visible. A hidden tab asks nothing. An expired session goes to sign-in.
 */
export const useMyGames = (
  api: GamesApi,
  { pollMs = MY_GAMES_POLL_MS, visibility = documentVisibility }: UseMyGamesOptions = {},
): MyGamesState => {
  const { goToSignIn } = useSession();
  const [games, setGames] = useState<GamesList>();
  const [status, setStatus] = useState<MyGamesStatus>('loading');
  const loaded = useRef(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const list = await api.myGames();
      loaded.current = true;
      if (mounted.current) {
        setGames(list);
        setStatus('ready');
      }
    } catch (error) {
      if (lobbyFailure(error).kind === 'unauthenticated') {
        goToSignIn();
        return;
      }
      logger.warn({ message: 'Loading the games list failed', error: (error as Error).message });
      if (mounted.current && !loaded.current) {
        setStatus('load-error');
      }
    }
  }, [api, goToSignIn]);

  useEffect(() => {
    mounted.current = true;
    void load();
    const whenVisible = () => {
      if (visibility.isVisible()) void load();
    };
    const unsubscribe = visibility.subscribe(whenVisible);
    const timer = setInterval(whenVisible, pollMs);

    return () => {
      mounted.current = false;
      unsubscribe();
      clearInterval(timer);
    };
  }, [load, pollMs, visibility]);

  const refresh = useCallback(() => {
    if (!loaded.current) setStatus('loading');
    void load();
  }, [load]);

  return { status, games, refresh };
};
